import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import type {
  EmailVerifyWithdrawalRequestError,
  EmailVerifyWithdrawalRequestResponse,
  EnumConfirmationStatus,
  CancelWithdrawalRequestError,
  CancelWithdrawalRequestResponse,
} from "@/api/cashier";
import {
  emailVerifyWithdrawalRequestOptions,
  cancelWithdrawalRequestOptions,
} from "@/api/cashier/@tanstack/react-query.gen";

export interface UseEmailVerificationProps {
  withdrawalRequestId?: string;
  emailVerificationToken?: string;
  enabled?: boolean;
  shouldCancel?: boolean; // New prop to trigger cancellation
}

export interface UseEmailVerificationReturn {
  verificationResult?: EmailVerifyWithdrawalRequestResponse;
  isLoading: boolean;
  error: EmailVerifyWithdrawalRequestError | null;
  refetch: () => void;
  confirmationStatus?: EnumConfirmationStatus;
  cancelResult?: CancelWithdrawalRequestResponse;
  isCancelling: boolean;
  cancelError: CancelWithdrawalRequestError | null;
}

/**
 * Custom hook for email verification of withdrawal requests
 * Automatically calls the email-verify endpoint when user comes from email link
 * Also handles cancellation when shouldCancel is true
 * @param withdrawalRequestId - The withdrawal request ID from URL params
 * @param enabled - Whether to enable the verification query (default: false)
 * @param shouldCancel - Whether to trigger cancellation (default: false)
 * @returns Object containing verification result, loading state, error, refetch function, confirmation status, and cancel state
 */
export const useEmailVerification = ({
  withdrawalRequestId,
  emailVerificationToken,
  enabled = false,
  shouldCancel = false,
}: UseEmailVerificationProps): UseEmailVerificationReturn => {
  const hasCalledCancelRef = useRef(false);

  const {
    data: verificationResult,
    isLoading,
    error,
    refetch,
  } = useQuery({
    ...emailVerifyWithdrawalRequestOptions({
      path: {
        withdrawalRequestID: withdrawalRequestId ?? "",
      },
      query: {
        emailVerificationToken: emailVerificationToken ?? "",
      },
    }),
    enabled: enabled && !!withdrawalRequestId && !!emailVerificationToken,
  });

  const {
    data: cancelResult,
    isLoading: isCancelling,
    error: cancelError,
  } = useQuery({
    ...cancelWithdrawalRequestOptions({
      path: {
        withdrawalRequestID: withdrawalRequestId ?? "",
      },
    }),
    enabled:
      shouldCancel && !hasCalledCancelRef.current && !!withdrawalRequestId,
  });

  // Track that cancel has been called to prevent duplicate calls
  useEffect(() => {
    if (shouldCancel && !hasCalledCancelRef.current) {
      hasCalledCancelRef.current = true;
    }
  }, [shouldCancel]);

  return {
    verificationResult,
    isLoading,
    error: error as EmailVerifyWithdrawalRequestError | null,
    refetch,
    confirmationStatus: verificationResult?.confirmation_status,
    cancelResult,
    isCancelling,
    cancelError: cancelError as CancelWithdrawalRequestError | null,
  };
};
